import HttpFactory from '~/repository/factory'
import type {
  CreatePortabilidadePayload,
  BatchCreatePortabilidadePayload,
  FullPortabilidadeResponse,
  Portabilidade,
  UpdatePortabilidadePayload,
  SavePortabilidadeFundosPayload,
  SubmitPortabilidadePayload,
  SubmitPortabilidadeResponse,
  CertificadoListItem,
  CertificadoDetalhe,
  CedenteInfo,
  CedentePlanoResponse,
  CertificadoElegivel,
  CnpjLookupResponse,
} from '~/types/portabilidade'

class PortabilidadeModule extends HttpFactory {
  private RESOURCE = '/portabilidade'

  async findByLeadId(leadId: string): Promise<Portabilidade[]> {
    return await this.get<Portabilidade[]>(`${this.RESOURCE}/by-lead/${leadId}`)
  }

  async create(data: CreatePortabilidadePayload): Promise<{ portabilidade: Portabilidade; proposal: { id: string } }> {
    return await this.post<{ portabilidade: Portabilidade; proposal: { id: string } }>(this.RESOURCE, data)
  }

  async batchCreate(data: BatchCreatePortabilidadePayload): Promise<{ portabilidades: Portabilidade[]; proposals: { id: string }[]; primaryId: string }> {
    return await this.post<{ portabilidades: Portabilidade[]; proposals: { id: string }[]; primaryId: string }>(`${this.RESOURCE}/batch`, data)
  }

  async getFullPortabilidade(id: string): Promise<FullPortabilidadeResponse> {
    return await this.get<FullPortabilidadeResponse>(`${this.RESOURCE}/${id}`)
  }

  async getByProposalId(proposalId: string): Promise<FullPortabilidadeResponse> {
    return await this.get<FullPortabilidadeResponse>(`${this.RESOURCE}/by-proposal/${proposalId}`)
  }

  async update(id: string, data: UpdatePortabilidadePayload): Promise<Portabilidade> {
    return await this.put<Portabilidade>(`${this.RESOURCE}/${id}`, data)
  }

  async saveFundos(id: string, data: SavePortabilidadeFundosPayload): Promise<void> {
    await this.post<void>(`${this.RESOURCE}/${id}/fundos`, data)
  }

  // ── Standalone portabilidade methods ─────────────────────────────────────

  async submitToIcatu(id: string, data: SubmitPortabilidadePayload): Promise<SubmitPortabilidadeResponse> {
    return await this.post<SubmitPortabilidadeResponse>(`${this.RESOURCE}/${id}/submit`, data)
  }

  async getTermoPdf(id: string, idTermo: string): Promise<{ arquivo: string }> {
    return await this.get<{ arquivo: string }>(`${this.RESOURCE}/${id}/termos/${idTermo}/pdf`)
  }

  async getCertificados(cpf: string): Promise<CertificadoListItem[]> {
    return await this.get<CertificadoListItem[]>(`${this.RESOURCE}/certificados/${cpf}`)
  }

  async getCertificadoDetalhes(cpf: string, idCertificado: string): Promise<CertificadoDetalhe> {
    return await this.get<CertificadoDetalhe>(`${this.RESOURCE}/certificados/${cpf}/${idCertificado}`)
  }

  // ── Icatu lookups (cedentes, certificados elegíveis) ─────────────────────

  async listarCertificadosElegiveis(idCliente: string): Promise<CertificadoElegivel[]> {
    return await this.get<CertificadoElegivel[]>(`${this.RESOURCE}/certificados-elegiveis/${idCliente}`)
  }

  async getCedentesPlanos(processoSusep: string): Promise<CedentePlanoResponse[]> {
    return await this.get<CedentePlanoResponse[]>(`${this.RESOURCE}/cedentes`, {
      query: { processoSusep },
    })
  }

  async lookupByCnpjFundo(cnpj: string, tipoPlano?: string): Promise<CnpjLookupResponse | null> {
    return await this.get<CnpjLookupResponse | null>(`${this.RESOURCE}/lookup-by-cnpj`, {
      query: { cnpj, ...(tipoPlano && { tipoPlano }) },
    })
  }
}

export default PortabilidadeModule
